import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit, Eye, Trash2, Users, Mail, Clock, CheckCircle, DollarSign, Filter, MoreHorizontal, TrendingUp, Calendar, Grid, List, Link as LinkIcon, Copy, Check, Inbox } from 'lucide-react';
import AddPartnershipModal from './AddPartnershipModal';
import ViewPartnershipModal from './ViewPartnershipModal';
import { usePartnershipsStore, Partnership } from '../../store/partnershipsStore';
import { partnershipsService } from '../../services/partnerships.service';

const PartnershipsPage: React.FC = () => {
  const {
    partnerships,
    isLoading,
    searchTerm,
    filterStage,
    setSearchTerm,
    setFilterStage,
    fetchPartnerships,
    deletePartnership
  } = usePartnershipsStore();

  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedPartnership, setSelectedPartnership] = useState<Partnership | null>(null);
  const [viewMode, setViewMode] = useState<'table' | 'cards'>('cards');
  const [viewingPartnership, setViewingPartnership] = useState<Partnership | null>(null);
  const [publicLink, setPublicLink] = useState<string | null>(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);

  useEffect(() => {
    fetchPartnerships();
  }, [fetchPartnerships]);

  const stages = ['Inbound Request', 'Lead', 'Pitch Sent', 'Contract Sent', 'Content Delivered', 'Payment Received'];

  // Helper function to format dates to dd-mm-yyyy hh:mm AM/PM
  const formatDate = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '';

    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) return '';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      const hours = date.getHours();
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hours12 = hours % 12 || 12;

      return `${day}-${month}-${year} ${hours12}:${minutes} ${ampm}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  // Helper function to format dates to just dd-mm-yyyy (for delivery dates)
  const formatDateOnly = (dateString: string | Date | null | undefined): string => {
    if (!dateString) return '';

    try {
      const date = typeof dateString === 'string' ? new Date(dateString) : dateString;
      if (isNaN(date.getTime())) return '';

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      'Inbound Request': 'from-cyan-400 to-cyan-600 text-white',
      'Lead': 'from-gray-400 to-gray-500 text-white',
      'Pitch Sent': 'from-primary-400 to-primary-600 text-white',
      'Contract Sent': 'from-yellow-400 to-orange-500 text-white',
      'Content Delivered': 'from-purple-400 to-purple-600 text-white',
      'Payment Received': 'from-green-400 to-green-600 text-white'
    };
    return colors[stage] || 'from-gray-400 to-gray-500 text-white';
  };

  const getStatusIcon = (stage: string) => {
    switch (stage) {
      case 'Inbound Request': return <Inbox className="w-4 h-4" />;
      case 'Lead': return <Users className="w-4 h-4" />;
      case 'Pitch Sent': return <Mail className="w-4 h-4" />;
      case 'Contract Sent': return <Clock className="w-4 h-4" />;
      case 'Content Delivered': return <CheckCircle className="w-4 h-4" />;
      case 'Payment Received': return <DollarSign className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400 border-red-200 dark:border-red-800';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
      case 'low': return 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 border-green-200 dark:border-green-800';
      default: return 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    }
  };

  const handleEditPartnership = (partnership: Partnership) => {
    setSelectedPartnership(partnership);
    setShowAddModal(true);
  };

  const handleCloseModal = () => {
    setShowAddModal(false);
    setSelectedPartnership(null);
  };
  const handleViewPartnership = (partnership: Partnership) => {
    setViewingPartnership(partnership);
  };

  const handleGeneratePublicLink = async () => {
    setIsGeneratingLink(true);
    try {
      const result = await partnershipsService.generatePublicLink();
      setPublicLink(result.link);
    } catch (error) {
      console.error('Failed to generate public link:', error);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  const handleCopyLink = async () => {
    if (!publicLink) return;
    try {
      await navigator.clipboard.writeText(publicLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const filteredPartnerships = partnerships.filter(partnership => {
    const matchesSearch = partnership.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partnership.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
      partnership.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (partnership.tags && partnership.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase())));
    const matchesStage = filterStage === 'all' || partnership.stage === filterStage;
    return matchesSearch && matchesStage;
  });

  const totalValue = partnerships.reduce((sum, p) => sum + p.value, 0);
  const completedValue = partnerships.filter(p => p.stage === 'Payment Received').reduce((sum, p) => sum + p.value, 0);
  const pendingValue = totalValue - completedValue;

  if (isLoading) {
    return (
      <div className="h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Enhanced Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Partnerships</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage your brand partnerships and collaborations</p>
        </div>

        {/* Stats Overview */}
        <div className="grid grid-cols-3 gap-4 w-full max-w-lg">
          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Pipeline</div>
            <div className="text-xl font-bold text-gray-900 dark:text-white text-center break-all">
              ${totalValue.toLocaleString()}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Completed</div>
            <div className="text-xl font-bold text-green-600 text-center break-all">
              ${completedValue.toLocaleString()}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-3xl p-4 shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 flex flex-col items-center space-y-2 min-w-0">
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending</div>
            <div className="text-xl font-bold text-orange-600 text-center break-all">
              ${pendingValue.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Filters & Actions */}
      <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-6">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                placeholder="Search partnerships, contacts, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                className="px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-gray-50 dark:bg-gray-700 focus:bg-white dark:focus:bg-gray-600 text-gray-900 dark:text-gray-100 transition-colors"
              >
                <option value="all">All Stages</option>
                {stages.map(stage => (
                  <option key={stage} value={stage}>{stage}</option>
                ))}
              </select>
              <button className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors">
                <Filter className="w-4 h-4" />
                <span className="hidden sm:inline">More Filters</span>
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
              <button
                onClick={() => setViewMode('cards')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'cards' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <Grid className="w-4 h-4" />
                Grid
              </button>
              <button
                onClick={() => setViewMode('table')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${viewMode === 'table' ? 'bg-white dark:bg-gray-600 text-gray-900 dark:text-white shadow-sm' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
              >
                <List className="w-4 h-4" />
                List
              </button>
            </div>
            <button
              onClick={handleGeneratePublicLink}
              disabled={isGeneratingLink}
              className="flex items-center gap-2 px-4 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors disabled:opacity-50"
            >
              <LinkIcon className="w-4 h-4" />
              {isGeneratingLink ? 'Generating...' : 'Get Public Link'}
            </button>
            <button
              onClick={() => {
                setSelectedPartnership(null);
                setShowAddModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-indigo-600 text-white rounded-xl hover:from-primary-700 hover:to-indigo-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              <Plus className="w-4 h-4" />
              New Partnership
            </button>
          </div>
        </div>
      </div>

      {/* Content Display */}
      {filteredPartnerships.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 p-12 text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-primary-600 dark:text-primary-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No partnerships found</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            {partnerships.length === 0
              ? "You don't have any partnerships yet. Create your first one to get started building your network."
              : "Try adjusting your search or filter to find what you're looking for."}
          </p>
          <button
            onClick={() => {
              setSelectedPartnership(null);
              setShowAddModal(true);
            }}
            className="px-6 py-3 bg-gradient-to-r from-primary-600 to-purple-600 text-white rounded-xl hover:from-primary-700 hover:to-purple-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            Create Partnership
          </button>
        </div>
      ) : (
        <>
          {viewMode === 'cards' ? (
            /* Cards View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredPartnerships.map(partnership => (
                <div key={partnership.id} className="group bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 hover:shadow-[0_0_20px_rgba(0,0,0,0.1)] transition-all duration-200 overflow-hidden flex flex-col h-full">
                  <div className="p-6 flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                          {partnership.brand}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{partnership.contact}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">{partnership.email}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        {partnership.priority && (
                          <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(partnership.priority)}`}>
                            {partnership.priority}
                          </span>
                        )}
                        <button className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                          <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        </button>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStageColor(partnership.stage)}`}>
                          {getStatusIcon(partnership.stage)}
                          <span className="ml-1">{partnership.stage}</span>
                        </span>
                        <div className="text-right">
                          <div className="text-lg font-bold text-gray-900 dark:text-white">${partnership.value.toLocaleString()}</div>
                        </div>
                      </div>

                      <div className="flex items-center justify-between text-sm">
                        {partnership.lastUpdated && (
                          <span className="text-gray-500 dark:text-gray-400 text-xs">
                            {formatDate(partnership.lastUpdated)}
                          </span>
                        )}
                      </div>

                      {partnership.tags && partnership.tags.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {partnership.tags.slice(0, 2).map(tag => (
                            <span key={tag} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                              {tag}
                            </span>
                          ))}
                          {partnership.tags.length > 2 && (
                            <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-md text-xs">
                              +{partnership.tags.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-100 dark:border-gray-600">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditPartnership(partnership)}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-all"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleViewPartnership(partnership)}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-all"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => deletePartnership(partnership.id)}
                          className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                      {partnership.deliveryDate && (
                        <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                          <Calendar className="w-3 h-3" />
                          <span>{formatDateOnly(partnership.deliveryDate)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-[0_0_15px_rgba(0,0,0,0.07)] border border-gray-100 dark:border-gray-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-600">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Brand & Contact</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Stage</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Value</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Priority</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Delivery Date</th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {filteredPartnerships.map(partnership => (
                      <tr key={partnership.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-semibold text-sm mr-3">
                              {partnership.brand.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{partnership.brand}</p>
                              <p className="text-sm text-gray-600 dark:text-gray-400">{partnership.contact}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-500">{partnership.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r ${getStageColor(partnership.stage)}`}>
                            {getStatusIcon(partnership.stage)}
                            <span className="ml-1">{partnership.stage}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
                            <span className="text-lg font-semibold text-gray-900 dark:text-white">${partnership.value.toLocaleString()}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {partnership.priority && (
                            <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getPriorityColor(partnership.priority)}`}>
                              {partnership.priority}
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-600 dark:text-gray-400">
                            {partnership.deliveryDate ? formatDateOnly(partnership.deliveryDate) : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleEditPartnership(partnership)}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-primary-600 dark:hover:text-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/30 rounded-lg transition-all"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleViewPartnership(partnership)}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-green-600 dark:hover:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 rounded-lg transition-all"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => deletePartnership(partnership.id)}
                              className="p-1.5 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 rounded-lg transition-all"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* Add Partnership Modal */}
      {showAddModal && (
        <AddPartnershipModal
          isOpen={showAddModal}
          onClose={handleCloseModal}
          partnership={selectedPartnership}
        />
      )}
      {/* View Partnership Modal */}
      {viewingPartnership && (
        <ViewPartnershipModal
          isOpen={!!viewingPartnership}
          onClose={() => setViewingPartnership(null)}
          partnership={viewingPartnership}
        />
      )}

      {/* Public Link Modal */}
      {publicLink && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            {/* Backdrop */}
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPublicLink(null)} />

            {/* Modal */}
            <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Public Partnership Form Link
                </h3>
                <button
                  onClick={() => setPublicLink(null)}
                  className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Share this link with brands to let them submit partnership proposals directly.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={publicLink}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm truncate"
                />
                <button
                  onClick={handleCopyLink}
                  className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${isCopied
                    ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                    }`}
                >
                  {isCopied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Copy
                    </>
                  )}
                </button>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                This link is permanent and unique to your team.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PartnershipsPage;